import { queryOptions } from "@tanstack/react-query";
import { config } from "@/config";
import type { ScriptReviewStatus } from "@/features/script-review/api/script-review.api";
import { PREFLIGHT_POLL_MED_MS } from "@/lib/query-constants";

// Public (guest) share payload for GET /sr/:token — no auth. Mirrors the shapes
// the backend whitelists (no tenant internals, no note createdByUserId).

export interface ShareReviewComment {
  id: string;
  type: string;
  text: string;
  exactSecond: number;
  sceneNumber: number | null;
  severity: string | null;
  /** Display-only for guests — they see resolved state but cannot change it. */
  resolvedAt: string | null;
}

export interface ShareReviewNote {
  id: string;
  aiCommentId: string | null;
  anchorSecond: number | null;
  authorName: string;
  authorKind?: string;
  body: string;
  resolvedAt: string | null;
  createdAt: string;
}

export interface ScriptReviewSharePayload {
  v: 1;
  id: string;
  status: ScriptReviewStatus;
  /** Version number of the shared cut (1 = original). */
  version: number;
  videoUrl: string | null;
  comments: ShareReviewComment[];
  notes: ShareReviewNote[];
  /** Comment-pass names that failed after retries. Empty when every pass succeeded. */
  failedPasses: string[];
}

export const scriptReviewShareKeys = {
  all: ["script-review-share"] as const,
  detail: (token: string) => [...scriptReviewShareKeys.all, token] as const,
};

async function fetchScriptReviewShare(token: string): Promise<ScriptReviewSharePayload> {
  // Public fetch. Same-origin `fetch` sends cookies by default, and a signed-in
  // Nexus session then fails the CSRF guard on the guest note POST.
  const res = await fetch(`${config.apiUrl}/sr/${encodeURIComponent(token)}`, {
    credentials: "omit",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Review not found (${res.status})`);
  }
  const json = (await res.json()) as { data: ScriptReviewSharePayload };
  return json.data;
}

/** Poll while the token already points at an in-progress tip; stop once analysis ends. */
export function scriptReviewSharePollInterval(
  status: ScriptReviewStatus | string | undefined,
): number | false {
  if (status === "QUEUED" || status === "ANALYZING") return PREFLIGHT_POLL_MED_MS;
  return false;
}

export function scriptReviewShareQueryOptions(token: string) {
  return queryOptions({
    queryKey: scriptReviewShareKeys.detail(token),
    queryFn: () => fetchScriptReviewShare(token),
    // Collaboration is interactive — always refetch so new comments appear.
    staleTime: 0,
    retry: 1,
    enabled: !!token,
    refetchInterval: (query) => scriptReviewSharePollInterval(query.state.data?.status),
  });
}

/** Post a guest comment/reply (name + body + target). Throws on failure. */
export async function postGuestNote(
  token: string,
  input: { name: string; body: string } & ({ aiCommentId: string } | { anchorSecond: number }),
): Promise<void> {
  const res = await fetch(`${config.apiUrl}/sr/${encodeURIComponent(token)}/notes`, {
    method: "POST",
    credentials: "omit",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Could not post the comment (${res.status})`);
  }
}
